import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, RotateCcw, Check } from "lucide-react";
import { useLead } from "@/context/LeadContext";
import { fireLead, trackEvent } from "@/lib/analytics";
import { SCRIPT, LOOP_MS } from "@/components/concierge/ConciergeHero";
import { CardByVariant } from "@/components/concierge/Cards";

const INTRO_SRC = "/media/demo-intro.mp4";
const ACT_TITLES = {
  1: "Stage the listing",
  2: "Pull the comps",
  3: "Capture the 360° tour",
  4: "Launch the campaign",
  5: "Score the lead",
  6: "Track the competition",
  7: "Stay ahead",
};

const useTypedText = (text, active, cpsMs = 32) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) { setN(text.length); return; }
    setN(0);
    let i = 0;
    const t = setInterval(() => { i += 1; setN(i); if (i >= text.length) clearInterval(t); }, cpsMs);
    return () => clearInterval(t);
  }, [text, active, cpsMs]);
  return text.slice(0, n);
};

const UserBubble = ({ text, live }) => {
  const shown = useTypedText(text, live);
  return (
    <div className="ml-auto max-w-[80%] bg-coral text-bone text-sm leading-snug px-4 py-2.5 rounded-2xl rounded-br-sm">
      {shown}{live && shown.length < text.length && <span className="inline-block w-[1px] h-3.5 bg-bone/80 ml-0.5 animate-pulse" />}
    </div>
  );
};

const AiBubble = ({ text }) => (
  <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.18 }} className="mr-auto max-w-[85%] bg-ink/5 text-ink text-sm leading-snug px-4 py-2.5 rounded-2xl rounded-bl-sm">
    {text}
  </motion.div>
);

const TypingDots = () => (
  <div className="mr-auto bg-ink/5 px-3 py-2.5 rounded-2xl rounded-bl-sm inline-flex gap-1">
    {[0, 1, 2].map((i) => (
      <motion.span key={i} animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.15 }} className="w-1.5 h-1.5 bg-ink/50 rounded-full" />
    ))}
  </div>
);

export const DemoModal = ({ open, onClose }) => {
  const { openLead } = useLead();
  const [phase, setPhase] = useState("intro"); // intro | chat | end
  const [introReady, setIntroReady] = useState(false); // false until we know if video is playable
  const [entries, setEntries] = useState([]);
  const [typing, setTyping] = useState(false);
  const [activeAct, setActiveAct] = useState(1);
  const [elapsed, setElapsed] = useState(0);
  const [runKey, setRunKey] = useState(0);
  const scrollRef = useRef(null);
  const videoRef = useRef(null);

  const allEntries = useMemo(() => SCRIPT.filter((s) => s.kind !== "typing").map((s, i) => ({ ...s, id: i })), []);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // On open, probe the intro video with a HEAD-ish check; skip to chat if unavailable
  useEffect(() => {
    if (!open) return;
    setPhase("intro"); setEntries([]); setTyping(false); setActiveAct(1); setElapsed(0);
    trackEvent("demo_modal_open");
    let cancelled = false;
    fetch(INTRO_SRC, { method: "HEAD" }).then((r) => {
      if (cancelled) return;
      if (r.ok) setIntroReady(true);
      else { setIntroReady(false); setPhase("chat"); }
    }).catch(() => { if (!cancelled) { setIntroReady(false); setPhase("chat"); } });
    return () => { cancelled = true; };
  }, [open, runKey]);

  // Chat timing loop (runs while phase === "chat")
  useEffect(() => {
    if (!open || phase !== "chat") return;
    const timeouts = [];
    setEntries([]); setTyping(false); setActiveAct(1);
    SCRIPT.forEach((step, idx) => {
      const t = setTimeout(() => {
        if (step.kind === "typing") {
          setTyping(true);
          const off = setTimeout(() => setTyping(false), 800);
          timeouts.push(off);
          return;
        }
        setTyping(false);
        setEntries((prev) => [...prev, { ...step, id: `${runKey}-${idx}` }]);
        if (step.act) setActiveAct(step.act);
      }, step.at * 1000);
      timeouts.push(t);
    });
    const endT = setTimeout(() => setPhase("end"), LOOP_MS + 1500);
    timeouts.push(endT);
    return () => timeouts.forEach(clearTimeout);
  }, [open, phase, runKey]);

  // 0:00 → 1:00 elapsed while in chat
  useEffect(() => {
    if (!open || phase !== "chat") { setElapsed(0); return; }
    let raf;
    const start = performance.now();
    const tick = (t) => {
      const s = Math.min(60, (t - start) / 1000);
      setElapsed(s);
      if (s < 60) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [open, phase, runKey]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [entries, typing]);

  const close = () => { onClose(); };
  const replay = () => setRunKey((k) => k + 1);
  const skipIntro = () => setPhase("chat");
  const startFree = () => {
    trackEvent("demo_cta_start_free");
    fireLead({ source: "demo-modal" });
    onClose();
    openLead({ source: "demo-modal" });
  };

  const mm = Math.floor(elapsed / 60);
  const ss = Math.floor(elapsed % 60);
  const clock = `${mm}:${String(ss).padStart(2, "0")}`;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-ink flex items-center justify-center"
          data-testid="demo-modal"
        >
          {/* Close */}
          <button onClick={close} aria-label="Close" data-testid="demo-modal-close" className="absolute top-5 right-5 z-30 text-bone/80 hover:text-bone p-2">
            <X className="w-6 h-6" />
          </button>

          {/* Phase 1 — Sora cinematic intro */}
          <AnimatePresence>
            {phase === "intro" && introReady && (
              <motion.div key="intro" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }} className="absolute inset-0" data-testid="demo-intro">
                <video
                  ref={videoRef}
                  src={INTRO_SRC}
                  autoPlay muted playsInline
                  onEnded={() => setPhase("chat")}
                  onError={() => setPhase("chat")}
                  className="w-full h-full object-cover"
                />
                {/* Cinematic overlay */}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-ink/40 via-transparent to-ink/70" />
                {/* Mid-video headline reveal (~4-10s of the 12s clip) */}
                <motion.div
                  className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center px-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 1, 1, 0] }}
                  transition={{ duration: 6, times: [0, 0.15, 0.75, 1], delay: 4, ease: "easeInOut" }}
                  data-testid="demo-intro-headline"
                >
                  <motion.p
                    initial={{ opacity: 0, letterSpacing: "0.1em" }}
                    animate={{ opacity: 1, letterSpacing: "0.35em" }}
                    transition={{ duration: 1.4, delay: 4, ease: "easeOut" }}
                    className="text-[10px] md:text-xs uppercase text-coral mb-4"
                  >
                    Chateaux
                  </motion.p>
                  <motion.p
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.1, delay: 4.6, ease: [0.22, 1, 0.36, 1] }}
                    className="font-serif text-3xl md:text-6xl lg:text-7xl text-bone/95 leading-[1.02]"
                  >
                    From lead to sale —
                  </motion.p>
                  <motion.p
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.1, delay: 5.6, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-1 md:mt-2 font-serif italic text-3xl md:text-6xl lg:text-7xl text-coral leading-[1.02]"
                  >
                    Chateaux does it all.
                  </motion.p>
                </motion.div>
                {/* Transition beat — fades in as the headline exits, hands off to the chat phase */}
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: [0, 0, 1, 1], y: [8, 8, 0, 0] }}
                  transition={{ duration: 2, times: [0, 0.5, 0.85, 1], delay: 10, ease: "easeOut" }}
                  className="pointer-events-none absolute inset-x-0 bottom-14 md:bottom-20 text-center"
                  data-testid="demo-intro-handoff"
                >
                  <p className="inline-flex items-center gap-2 text-sm md:text-base text-bone/85 tracking-wide">
                    <span className="h-px w-8 bg-coral" />
                    Now watch it happen
                    <span className="text-coral">→</span>
                  </p>
                </motion.div>
                <button onClick={skipIntro} data-testid="demo-skip-intro" className="absolute bottom-6 right-6 text-xs text-bone/60 hover:text-bone border border-bone/20 hover:border-bone/60 px-3 py-1.5 transition-colors">
                  Skip intro →
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Phase 2 — cinematic chat */}
          <AnimatePresence>
            {phase === "chat" && (
              <motion.div key="chat" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} className="relative w-full h-full flex flex-col items-center justify-center px-4 py-8 gap-6" data-testid="demo-chat">
                {/* Act caption */}
                <div className="text-center max-w-md">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-coral">Act {activeAct} of 7</p>
                  <p className="mt-2 font-serif text-2xl md:text-3xl text-bone/95">{ACT_TITLES[activeAct]}</p>
                </div>

                {/* Bigger phone */}
                <div className="relative w-[340px] sm:w-[380px] h-[640px] sm:h-[720px] rounded-[44px] bg-[#0f0f14] p-3 shadow-2xl">
                  <div className="absolute top-3 left-1/2 -translate-x-1/2 w-28 h-6 bg-[#0f0f14] rounded-b-2xl z-20" />
                  <div className="w-full h-full rounded-[34px] overflow-hidden bg-bone flex flex-col">
                    <div className="flex items-center gap-2 px-3 py-3 border-b border-ink/10 shrink-0">
                      <div className="w-7 h-7 rounded-full bg-coral flex items-center justify-center font-serif text-bone text-sm">C</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] text-ink font-medium leading-none">Concierge</p>
                        <p className="text-[9px] text-ink/50 mt-0.5 flex items-center gap-1"><span className="w-1.5 h-1.5 bg-green-500 rounded-full" /> online</p>
                      </div>
                      <span className="text-[10px] font-mono tabular-nums text-ink/50">{clock}</span>
                    </div>
                    <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2 scroll-smooth" data-testid="demo-transcript">
                      {entries.map((e) => (
                        <div key={e.id} className="flex flex-col">
                          {e.kind === "user" && <UserBubble text={e.text} live={e === entries[entries.length - 1]} />}
                          {e.kind === "ai" && <AiBubble text={e.text} />}
                          {e.kind === "card" && <div className="my-1"><CardByVariant variant={e.variant} /></div>}
                        </div>
                      ))}
                      {typing && <TypingDots />}
                    </div>
                    <div className="border-t border-ink/10 px-3 py-2.5 flex items-center gap-2 shrink-0">
                      <span className="flex-1 text-[11px] text-ink/40">ask anything…</span>
                      <div className="w-6 h-6 rounded-full bg-ink flex items-center justify-center text-bone text-[10px]">▲</div>
                    </div>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="w-[340px] sm:w-[380px] h-0.5 bg-bone/10 relative">
                  <div className="absolute inset-y-0 left-0 bg-coral" style={{ width: `${(elapsed / 60) * 100}%`, transition: "width 0.1s linear" }} />
                </div>

                <div className="flex items-center gap-3">
                  <button onClick={replay} data-testid="demo-replay" className="inline-flex items-center gap-1.5 text-xs text-bone/60 hover:text-bone border border-bone/20 hover:border-bone/60 px-3 py-1.5 transition-colors">
                    <RotateCcw className="w-3.5 h-3.5" /> Replay
                  </button>
                  <button onClick={() => setPhase("end")} className="text-xs text-bone/50 hover:text-bone/80 transition-colors">
                    Skip to end →
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Phase 3 — end card */}
          <AnimatePresence>
            {phase === "end" && (
              <motion.div key="end" initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }} className="relative w-full max-w-2xl px-8 text-center" data-testid="demo-end">
                <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[560px] h-[560px] bg-coral/10 blur-3xl rounded-full" aria-hidden="true" />
                <div className="relative w-14 h-14 border border-bone/20 mx-auto flex items-center justify-center mb-8">
                  <Check className="w-6 h-6 text-coral" />
                </div>
                <p className="text-xs uppercase tracking-[0.3em] text-coral mb-5">The whole listing — one chat</p>
                <h3 className="font-serif text-4xl md:text-6xl text-bone leading-[1.02]">
                  Now do it with <span className="italic text-coral">your</span> listing.
                </h3>
                <p className="mt-6 max-w-md mx-auto text-sm md:text-base text-bone/60 leading-relaxed">
                  Staged. Priced. 360'd. Launched. Scored. Competitor-tracked. In one conversation — with the concierge doing the work.
                </p>
                <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
                  <button onClick={startFree} data-testid="demo-end-cta" className="group inline-flex items-center gap-2 bg-coral text-bone px-8 py-4 text-sm font-medium hover:bg-bone hover:text-ink transition-colors duration-300">
                    Start free — no credit card <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </button>
                  <button onClick={replay} className="inline-flex items-center gap-1.5 text-sm text-bone/70 hover:text-bone border border-bone/20 hover:border-bone/60 px-6 py-4 transition-colors">
                    <RotateCcw className="w-4 h-4" /> Watch again
                  </button>
                </div>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1.2, delay: 1.6, ease: "easeOut" }}
                  className="mt-10 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-bone/40"
                  data-testid="demo-end-signature"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-coral animate-pulse" />
                  Recorded live · Chateaux · {new Date().toLocaleString("en-US", { month: "short", year: "numeric" })}
                </motion.p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
